import { useMemo, useState, type CSSProperties } from "react";
import { Card, Group, Stack, Text, Badge, Button, Avatar, Flex, Box, ActionIcon, Anchor } from "@mantine/core";
import {
  IconBookmark,
  IconMapPin,
  IconBuilding,
  IconRosetteDiscountCheckFilled,
  IconEye,
  IconPhone,
  IconMail,
  IconWorld,
  IconExternalLink,
} from "@tabler/icons-react";
import { useAuth } from "../../hooks/useAuth";
import ShortlistModal from "../shared/UserSignupModal";
import { Company } from "@gt/prisma";
import { getIndustryLabel } from "./constants/general.const";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { formatCompanyProfileTextForDisplay } from "@/shared/utils/capitalizeLeadingCharacter";
import {
  getCompanyWebsiteDisplayLabel,
  hasCompanyWebsite,
} from "@/shared/utils/companyWebsiteDisplay.util";

const COLLAB_LIST_CARD_STYLE: CSSProperties = {
  border: "1px solid #04305c",
  backgroundColor: "#ebf0ff",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
};

const COLLAB_OPEN_WORKSPACE_BTN_STYLES = {
  root: {
    backgroundColor: "#ffc300",
    color: "#04305c",
    border: "1px solid #ffc300",
    "&:hover": {
      backgroundColor: "#e6b000",
      borderColor: "#e6b000",
    },
  },
};

export interface CompanyWithUI extends Company {
  isShortlisted?: boolean;
  hasExistingRequest?: boolean;
}

export interface CompanyCardHorizontalProps {
  company: CompanyWithUI;
  onShortlistClick: (companyId: string, isShortlisted: boolean) => void;
  onGuestShortlistClick?: (companyId: string) => void;
  onViewDetails: (companyId: string) => void;
  showShortlistButton?: boolean;
  isLoading?: boolean;
  isShortlist?: boolean;
  isCollaborationList?: boolean;
  isDiscoverStyle?: boolean;

  collaborationOpenWindowNotice?: { dateLabel: string; daysLeft: number } | null;
}

const DISCOVER_BOOKMARK_BTN_STYLES = {
  root: {
    backgroundColor: "#04305c",
    color: "white",
    border: "1px solid #04305c",
    "&:hover": {
      backgroundColor: "#032847",
      borderColor: "#032847",
    },
  },
};

export default function CompanyCardHorizontal({
  company,
  onShortlistClick,
  onGuestShortlistClick,
  onViewDetails,
  showShortlistButton = true,
  isShortlist = false,
  isCollaborationList = false,
  isDiscoverStyle = false,
  collaborationOpenWindowNotice = null,
}: CompanyCardHorizontalProps) {
  const { isAuthenticated } = useAuth();
  const [modalOpened, setModalOpened] = useState(false);

  const handleShortlistClick = () => {
    if (!isAuthenticated) {
      onGuestShortlistClick?.(company.id);
      setModalOpened(true);
      return;
    }
    onShortlistClick(company.id, !company.isShortlisted);
  };

  const handleViewDetails = () => {
    onViewDetails(company.id);
  };

  const useMatchedListStyle = isCollaborationList || isDiscoverStyle;

  const headquartersFlagEmoji = useMemo(
    () => (isDiscoverStyle ? flagEmojiFromHeadquartersCountry(company.headquartersCountry) : null),
    [isDiscoverStyle, company.headquartersCountry],
  );

  // Use presigned URL from API response if available (for collaboration list)
  const logoUrl = isCollaborationList ? (company as any).logoPresignedUrl : null;

  return (
    <>
      <Card
        p="lg"
        radius="lg"
        withBorder={!useMatchedListStyle}
        style={useMatchedListStyle ? COLLAB_LIST_CARD_STYLE : undefined}
      >
        <Flex gap={{ base: "sm", sm: "md" }} direction={{ base: "column", sm: "row" }} align="stretch">
          {/* Avatar - Desktop only */}
          <Avatar
            size="lg"
            radius="md"
            variant={useMatchedListStyle ? "default" : "light"}
            color={useMatchedListStyle ? undefined : "primary"}
            visibleFrom="sm"
            src={logoUrl}
            styles={
              useMatchedListStyle
                ? {
                  root: {
                    border: "1px solid #04305c",
                    backgroundColor: "#F9FAFB",
                  },
                }
                : undefined
            }
          >
            <IconBuilding size={24} color={useMatchedListStyle ? "#6B7280" : undefined} />
          </Avatar>

          {/* Content */}
          <Stack gap="sm" style={{ flex: 1 }}>
            {isCollaborationList && collaborationOpenWindowNotice ? (
              <Box
                p="sm"
                mb={4}
                style={{
                  borderRadius: 8,
                  border: "1px solid #04305c",
                  backgroundColor: "rgba(235, 240, 255, 0.85)",
                }}
              >
                <Text size="sm" c="dark.7" lh={1.55}>
                  The collaboration will close on <strong>{collaborationOpenWindowNotice.dateLabel}</strong>.
                  {/* {collaborationOpenWindowNotice.daysLeft > 0
                    ? ` (${collaborationOpenWindowNotice.daysLeft} day${
                        collaborationOpenWindowNotice.daysLeft === 1 ? "" : "s"
                      } left in the open period). `
                    : " (last day of the open period). "} */}
                </Text>
              </Box>
            ) : null}
            <Flex justify="space-between" align="center">
              <Group gap="sm" align="center">
                {headquartersFlagEmoji ? (
                  <Text
                    component="span"
                    fz={{ base: 18, sm: 20 }}
                    lh={1}
                    role="img"
                    aria-label={`${company.headquartersCountry ?? "Country"} flag`}
                    style={{ flexShrink: 0 }}
                  >
                    {headquartersFlagEmoji}
                  </Text>
                ) : null}
                <Anchor
                  onClick={handleViewDetails}
                  td="none"
                  c="inherit"
                  styles={{
                    root: {
                      "&:hover": {
                        textDecoration: "underline",
                        cursor: "pointer",
                      },
                    },
                  }}
                >
                  <Text fw={400} fz={{ base: "md", sm: "lg" }} c={useMatchedListStyle ? "#04305c" : undefined}>
                    {isCollaborationList
                      ? (company.legalName ?? company.gtid ?? "Company")
                      : `${company.gtid}`}
                  </Text>
                </Anchor>
                {company.status === "VERIFIED" && (
                  <IconRosetteDiscountCheckFilled size={18} style={{ color: "var(--mantine-color-primary-6)" }} />
                )}
              </Group>

              {/* Mobile Actions - Only show heart if NOT in shortlist page */}
              {showShortlistButton && !isShortlist && (
                <Box hiddenFrom="sm">
                  <Button
                    variant={isDiscoverStyle ? "filled" : "outline"}
                    color={isDiscoverStyle ? undefined : company.isShortlisted ? "yellow" : "gray"}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={
                      isDiscoverStyle
                        ? DISCOVER_BOOKMARK_BTN_STYLES
                        : { root: { borderColor: company.isShortlisted ? "#FEE685" : undefined } }
                    }
                  >
                    <IconBookmark
                      size={18}
                      color={isDiscoverStyle ? "white" : undefined}
                      fill={company.isShortlisted ? "currentColor" : "none"}
                    />
                  </Button>
                </Box>
              )}
              {/* Mobile — shortlist remove (same chrome as Discover bookmark when isDiscoverStyle) */}
              {showShortlistButton && isShortlist && isDiscoverStyle && (
                <Box hiddenFrom="sm">
                  <Button
                    variant="filled"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={DISCOVER_BOOKMARK_BTN_STYLES}
                  >
                    <IconBookmark size={18} color="white" fill="currentColor" />
                  </Button>
                </Box>
              )}
              {showShortlistButton && isShortlist && !isDiscoverStyle && (
                <Box hiddenFrom="sm">
                  <Button
                    variant="outline"
                    color="yellow"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={{ root: { borderColor: "#FEE685" } }}
                  >
                    <IconBookmark size={18} fill="currentColor" />
                  </Button>
                </Box>
              )}
            </Flex>

            <Group gap="md" wrap="wrap">
              {isCollaborationList ? (
                <>
                  {(company as any).subDescription ? (
                    <Text size="sm" c="dark.7" lh={1.5} lineClamp={2} w="100%">
                      {formatCompanyProfileTextForDisplay((company as any).subDescription)}
                    </Text>
                  ) : null}
                  {((company as any).headquartersCity || company.headquartersCountry) && (
                    <Group gap={4}>
                      <IconMapPin size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {(company as any).headquartersCity && company.headquartersCountry
                          ? `${(company as any).headquartersCity}, ${company.headquartersCountry}`
                          : company.headquartersCountry || (company as any).headquartersCity}
                      </Text>
                    </Group>
                  )}
                  {((company as any).phoneCountryCode || (company as any).phoneNumber) && (
                    <Group gap={4}>
                      <IconPhone size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {(company as any).phoneCountryCode && (company as any).phoneNumber
                          ? `${(company as any).phoneCountryCode} ${(company as any).phoneNumber}`
                          : (company as any).phoneNumber || "N/A"}
                      </Text>
                    </Group>
                  )}
                  {(company as any).email && (
                    <Group gap={4}>
                      <IconMail size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {(company as any).email}
                      </Text>
                    </Group>
                  )}
                </>
              ) : (
                <>
                  <Group gap={4}>
                    <IconBuilding size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                    {Array.isArray(company.industryCategory) && company.industryCategory.length > 0 ? (
                      <Group gap={4}>
                        <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                          {getIndustryLabel(company.industryCategory[0])}
                        </Text>
                        {company.industryCategory.length > 1 && (
                          <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                            +{company.industryCategory.length - 1}
                          </Text>
                        )}
                      </Group>
                    ) : (
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        N/A
                      </Text>
                    )}
                  </Group>
                  {company.headquartersCountry && (
                    <Group gap={4}>
                      <IconMapPin size={14} style={{ color: "var(--mantine-color-slate-6)" }} />
                      <Text fz={{ base: "xs", sm: "sm" }} c="slate.6">
                        {company.headquartersCountry}
                      </Text>
                    </Group>
                  )}
                </>
              )}
            </Group>

            {!isCollaborationList && (
              <>
                <Text size="sm" c="dark.7" lh={1.5} lineClamp={2}>
                  {formatCompanyProfileTextForDisplay(
                    (company as any).subDescription || company.description,
                  )}
                </Text>
                {Array.isArray((company as any).operations) && (company as any).operations.length > 0 && (
                  <Group gap="xs" wrap="wrap">
                    {(company as any).operations.slice(0, 3).map((op: string, idx: number) => (
                      <Badge
                        key={`${op}-${idx}`}
                        variant="light"
                        color="indigo"
                        size="sm"
                        radius="md"
                        fw={500}
                        styles={{ root: { textTransform: "none" } }}
                      >
                        {op}
                      </Badge>
                    ))}
                    {(company as any).operations.length > 3 && (
                      <Badge
                        variant="light"
                        color="gray"
                        size="sm"
                        radius="md"
                        fw={500}
                        styles={{ root: { textTransform: "none" } }}
                      >
                        +{(company as any).operations.length - 3} more
                      </Badge>
                    )}
                  </Group>
                )}
              </>
            )}

            {isCollaborationList && (
              <Group gap="md" wrap="wrap">
                <Group gap={4} wrap="nowrap" miw={0} style={{ maxWidth: "100%" }}>
                  <IconWorld size={16} color="var(--mantine-color-primary-6)" />
                  {hasCompanyWebsite((company as any).website) ? (
                    <Anchor
                      href={(company as any).website.trim()}
                      target="_blank"
                      td="none"
                      style={{ minWidth: 0 }}
                      styles={{
                        root: {
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        },
                      }}
                    >
                      <Group gap={4} wrap="wrap">
                        <Text size="sm" c="primary.6" style={{ wordBreak: "break-all" }}>
                          {getCompanyWebsiteDisplayLabel((company as any).website)}
                        </Text>
                        <IconExternalLink size={12} color="var(--mantine-color-primary-6)" />
                      </Group>
                    </Anchor>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {getCompanyWebsiteDisplayLabel((company as any).website)}
                    </Text>
                  )}
                </Group>
              </Group>
            )}

            {/* Mobile Actions Full Width */}
            <Box hiddenFrom="sm" mt="sm">
              <Flex gap="sm" direction={{ base: "column", xs: "row" }}>
                {isShortlist && isDiscoverStyle ? (
                  <Button
                    size="sm"
                    radius="md"
                    fullWidth
                    styles={COLLAB_OPEN_WORKSPACE_BTN_STYLES}
                    leftSection={<IconEye size={16} color="#04305c" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Details
                  </Button>
                ) : isShortlist && !isDiscoverStyle ? (
                  <Button
                    variant="outline"
                    size="sm"
                    radius="md"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Profile
                  </Button>
                ) : isCollaborationList ? (
                  <Button
                    variant="filled"
                    size="sm"
                    radius="md"
                    fullWidth
                    styles={COLLAB_OPEN_WORKSPACE_BTN_STYLES}
                    leftSection={<IconEye size={16} color="#04305c" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    Open Workspace
                  </Button>
                ) : isDiscoverStyle ? (
                  <Button
                    variant="filled"
                    size="sm"
                    radius="md"
                    fullWidth
                    styles={COLLAB_OPEN_WORKSPACE_BTN_STYLES}
                    leftSection={<IconEye size={16} color="#04305c" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Details
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    color="primary"
                    size="sm"
                    radius="md"
                    fullWidth
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Details
                  </Button>
                )}
              </Flex>
            </Box>
          </Stack>

          {/* Desktop Right Side Actions */}
          <Box visibleFrom="sm">
            {isShortlist && isDiscoverStyle ? (
              <Group gap="sm" align="center">
                {showShortlistButton && (
                  <Button
                    variant="filled"
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={DISCOVER_BOOKMARK_BTN_STYLES}
                  >
                    <IconBookmark size={18} color="white" fill="currentColor" />
                  </Button>
                )}
                <Button
                  size="sm"
                  radius="md"
                  styles={COLLAB_OPEN_WORKSPACE_BTN_STYLES}
                  leftSection={<IconEye size={16} color="#04305c" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                >
                  View Details
                </Button>
              </Group>
            ) : isShortlist && !isDiscoverStyle ? (
              <Stack align="flex-end" justify="space-between" gap="md" style={{ minHeight: "100%" }}>
                {showShortlistButton && (
                  <ActionIcon
                    variant="outline"
                    color="yellow"
                    size="lg"
                    radius="md"
                    style={{ borderColor: "#FEE685" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                  >
                    <IconBookmark size={18} fill="currentColor" />
                  </ActionIcon>
                )}
                <Group gap="sm">
                  <Button
                    variant="default"
                    size="sm"
                    radius="md"
                    leftSection={<IconEye size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails();
                    }}
                  >
                    View Profile
                  </Button>
                </Group>
              </Stack>
            ) : isCollaborationList ? (
              <Group gap="sm" align="center">
                <Button
                  variant="filled"
                  size="sm"
                  radius="md"
                  styles={COLLAB_OPEN_WORKSPACE_BTN_STYLES}
                  leftSection={<IconEye size={16} color="#04305c" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                >
                  Open Workspace
                </Button>
              </Group>
            ) : (
              <Group gap="sm" align="center">
                {showShortlistButton && (
                  <Button
                    variant={isDiscoverStyle ? "filled" : "outline"}
                    color={isDiscoverStyle ? undefined : company.isShortlisted ? "yellow" : "gray"}
                    size="sm"
                    radius="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortlistClick();
                    }}
                    px="xs"
                    styles={
                      isDiscoverStyle
                        ? DISCOVER_BOOKMARK_BTN_STYLES
                        : { root: { borderColor: company.isShortlisted ? "#FEE685" : undefined } }
                    }
                  >
                    <IconBookmark
                      size={18}
                      color={isDiscoverStyle ? "white" : undefined}
                      fill={company.isShortlisted ? "currentColor" : "none"}
                    />
                  </Button>
                )}
                <Button
                  variant={isDiscoverStyle ? "filled" : "outline"}
                  color={isDiscoverStyle ? undefined : "primary"}
                  size="sm"
                  radius="md"
                  styles={isDiscoverStyle ? COLLAB_OPEN_WORKSPACE_BTN_STYLES : undefined}
                  leftSection={isDiscoverStyle ? <IconEye size={16} color="#04305c" /> : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails();
                  }}
                >
                  View Details
                </Button>
              </Group>
            )}
          </Box>
        </Flex>
      </Card>

      <ShortlistModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        companyName={company.gtid ? `${company.gtid}` : (company.legalName ?? "this company")}
      />
    </>
  );
}
